/**
 * Comprehensive Patient Data Management API Service
 * Handles biometrics, medical history, documents, and intake forms
 */
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Use proxy for development, fallback to config
const BASE_URL = process.env.NODE_ENV === 'development' ? '' : API_BASE_URL;
const COMPREHENSIVE_API = `${BASE_URL}/api/v1/patient/comprehensive`;

// Biometrics API
export const biometricsApi = {
  // Get biometrics data
  getBiometrics: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/biometrics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Parse the data if it exists
      if (response.data && response.data.length > 0) {
        return {
          ...response.data,
          data: response.data.map(item => parseBiometricsData(item))
        };
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching biometrics:', error);
      throw error;
    }
  },

  // Create or update biometrics
  saveBiometrics: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const formattedData = formatBiometricsData(data);
      
      // First check if biometrics already exists
      const existingResponse = await axios.get(`${COMPREHENSIVE_API}/biometrics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      let response;
      if (existingResponse.data && existingResponse.data.length > 0) {
        // Update existing record
        const existingId = existingResponse.data[0].id;
        response = await axios.put(`${COMPREHENSIVE_API}/biometrics/${existingId}/`, formattedData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // Create new record
        response = await axios.post(`${COMPREHENSIVE_API}/biometrics/`, formattedData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error saving biometrics:', error);
      throw error;
    }
  },

  // Update biometrics
  updateBiometrics: async (id, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const formattedData = formatBiometricsData(data);
      const response = await axios.put(`${COMPREHENSIVE_API}/biometrics/${id}/`, formattedData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating biometrics:', error);
      throw error;
    }
  },

  // Get biometrics summary
  getBiometricsSummary: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/biometrics/summary/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching biometrics summary:', error);
      throw error;
    }
  },

  // Get biometrics trends
  getBiometricsTrends: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/biometrics/trends/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching biometrics trends:', error);
      throw error;
    }
  }
};

// Medical History API
export const medicalHistoryApi = {
  // Get medical history
  getMedicalHistory: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/medical-history/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching medical history:', error);
      throw error;
    }
  },

  // Create medical history record
  createMedicalHistory: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${COMPREHENSIVE_API}/medical-history/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating medical history:', error);
      throw error;
    }
  },

  // Update medical history record
  updateMedicalHistory: async (id, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${COMPREHENSIVE_API}/medical-history/${id}/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating medical history:', error);
      throw error;
    }
  },

  // Get medical history summary
  getMedicalHistorySummary: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/medical-history/summary/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching medical history summary:', error);
      throw error;
    }
  }
};

// Documents API
export const documentsApi = {
  // Get documents
  getDocuments: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/documents/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  // Upload document
  uploadDocument: async (formData) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${COMPREHENSIVE_API}/documents/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // Get documents by type
  getDocumentsByType: async (documentType) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/documents/by_type/?type=${documentType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents by type:', error);
      throw error;
    }
  },

  // Get documents summary
  getDocumentsSummary: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/documents/summary/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents summary:', error);
      throw error;
    }
  },

  // Delete document
  deleteDocument: async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.delete(`${COMPREHENSIVE_API}/documents/${id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }
};

// Intake Form API
export const intakeFormApi = {
  // Get intake form
  getIntakeForm: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/intake-form/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching intake form:', error);
      throw error;
    }
  },

  // Create intake form
  createIntakeForm: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${COMPREHENSIVE_API}/intake-form/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating intake form:', error);
      throw error;
    }
  },

  // Update intake form
  updateIntakeForm: async (id, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${COMPREHENSIVE_API}/intake-form/${id}/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating intake form:', error);
      throw error;
    }
  },

  // Get intake form summary
  getIntakeFormSummary: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/intake-form/summary/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching intake form summary:', error);
      throw error;
    }
  },

  // Complete intake form
  completeIntakeForm: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${COMPREHENSIVE_API}/intake-form/complete/`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error completing intake form:', error);
      throw error;
    }
  }
};

// Dashboard API
export const dashboardApi = {
  // Get comprehensive dashboard data
  getDashboardData: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/dashboard/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // Get health metrics
  getHealthMetrics: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/health-metrics/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching health metrics:', error);
      throw error;
    }
  }
};

// Comprehensive Profile API
export const comprehensiveProfileApi = {
  // Get comprehensive profile
  getComprehensiveProfile: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${COMPREHENSIVE_API}/profile/comprehensive/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching comprehensive profile:', error);
      throw error;
    }
  },

  // Update comprehensive profile
  updateComprehensiveProfile: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${COMPREHENSIVE_API}/profile/comprehensive/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating comprehensive profile:', error);
      throw error;
    }
  }
};

// Utility functions
export const formatBiometricsData = (data) => {
  return {
    height_cm: data.heightCm && data.heightCm !== '' ? parseInt(data.heightCm) : null,
    weight_kg: data.weightKg && data.weightKg !== '' ? parseFloat(data.weightKg) : null,
    blood_pressure_systolic: data.bloodPressureSystolic && data.bloodPressureSystolic !== '' ? parseInt(data.bloodPressureSystolic) : null,
    blood_pressure_diastolic: data.bloodPressureDiastolic && data.bloodPressureDiastolic !== '' ? parseInt(data.bloodPressureDiastolic) : null,
    heart_rate: data.heartRate && data.heartRate !== '' ? parseInt(data.heartRate) : null,
    temperature: data.temperature && data.temperature !== '' ? parseFloat(data.temperature) : null,
    respiratory_rate: data.respiratoryRate && data.respiratoryRate !== '' ? parseInt(data.respiratoryRate) : null,
    waist_circumference: data.waistCircumference && data.waistCircumference !== '' ? parseFloat(data.waistCircumference) : null,
    hip_circumference: data.hipCircumference && data.hipCircumference !== '' ? parseFloat(data.hipCircumference) : null,
    body_fat_percentage: data.bodyFatPercentage && data.bodyFatPercentage !== '' ? parseFloat(data.bodyFatPercentage) : null,
    muscle_mass_kg: data.muscleMassKg && data.muscleMassKg !== '' ? parseFloat(data.muscleMassKg) : null,
    allergies: data.allergies || [],
    drug_allergies: data.drugAllergies || [],
    food_allergies: data.foodAllergies || [],
    smoking_status: data.smokingStatus,
    alcohol_consumption: data.alcoholConsumption,
    exercise_frequency: data.exerciseFrequency,
    sleep_hours: data.sleepHours && data.sleepHours !== '' ? parseFloat(data.sleepHours) : null
  };
};

export const parseBiometricsData = (data) => {
  return {
    id: data.id,
    heightCm: data.height_cm,
    weightKg: data.weight_kg,
    bloodPressureSystolic: data.blood_pressure_systolic,
    bloodPressureDiastolic: data.blood_pressure_diastolic,
    heartRate: data.heart_rate,
    temperature: data.temperature,
    respiratoryRate: data.respiratory_rate,
    waistCircumference: data.waist_circumference,
    hipCircumference: data.hip_circumference,
    bodyFatPercentage: data.body_fat_percentage,
    muscleMassKg: data.muscle_mass_kg,
    allergies: data.allergies || [],
    drugAllergies: data.drug_allergies || [],
    foodAllergies: data.food_allergies || [],
    smokingStatus: data.smoking_status,
    alcoholConsumption: data.alcohol_consumption,
    exerciseFrequency: data.exercise_frequency,
    sleepHours: data.sleep_hours,
    bmi: data.bmi,
    bmiCategory: data.bmi_category,
    waistToHipRatio: data.waist_to_hip_ratio,
    riskAssessment: data.risk_assessment,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  };
};

export const formatIntakeFormData = (data) => {
  return {
    chief_complaint: data.chiefComplaint,
    duration_of_symptoms: data.durationOfSymptoms,
    present_illness: data.presentIllness,
    symptom_onset: data.symptomOnset,
    symptom_severity: data.symptomSeverity,
    mood: data.mood,
    affect: data.affect,
    anxiety_level: data.anxietyLevel,
    depression_level: data.depressionLevel,
    sleep_duration: data.sleepDuration,
    sleep_quality: data.sleepQuality,
    sleep_issues: data.sleepIssues || [],
    appetite_changes: data.appetiteChanges,
    weight_changes: data.weightChanges,
    weight_change_amount: data.weightChangeAmount,
    energy_level: data.energyLevel,
    activity_level: data.activityLevel,
    concentration: data.concentration,
    memory: data.memory,
    decision_making: data.decisionMaking,
    suicidal_thoughts: data.suicidalThoughts,
    suicidal_plan: data.suicidalPlan,
    suicidal_intent: data.suicidalIntent,
    self_harm_thoughts: data.selfHarmThoughts,
    self_harm_behavior: data.selfHarmBehavior,
    homicidal_thoughts: data.homicidalThoughts,
    homicidal_plan: data.homicidalPlan,
    homicidal_intent: data.homicidalIntent,
    current_substance_use: data.currentSubstanceUse || {},
    substance_abuse_history: data.substanceAbuseHistory,
    treatment_history: data.treatmentHistory,
    social_functioning: data.socialFunctioning,
    occupational_functioning: data.occupationalFunctioning,
    relationship_status: data.relationshipStatus,
    trauma_history: data.traumaHistory,
    trauma_details: data.traumaDetails,
    ptsd_symptoms: data.ptsdSymptoms || [],
    cultural_background: data.culturalBackground,
    religious_beliefs: data.religiousBeliefs,
    cultural_factors: data.culturalFactors,
    treatment_goals: data.treatmentGoals || [],
    patient_expectations: data.patientExpectations,
    risk_factors: data.riskFactors || [],
    protective_factors: data.protectiveFactors || [],
    clinical_impression: data.clinicalImpression,
    differential_diagnosis: data.differentialDiagnosis || [],
    treatment_recommendations: data.treatmentRecommendations || [],
    medication_recommendations: data.medicationRecommendations || [],
    therapy_recommendations: data.therapyRecommendations || [],
    follow_up_plan: data.followUpPlan,
    urgency_level: data.urgencyLevel,
    is_complete: data.isComplete
  };
};



export const parseIntakeFormData = (data) => {
  return {
    chiefComplaint: data.chief_complaint,
    durationOfSymptoms: data.duration_of_symptoms,
    presentIllness: data.present_illness,
    symptomOnset: data.symptom_onset,
    symptomSeverity: data.symptom_severity,
    mood: data.mood,
    affect: data.affect,
    anxietyLevel: data.anxiety_level,
    depressionLevel: data.depression_level,
    sleepDuration: data.sleep_duration,
    sleepQuality: data.sleep_quality,
    sleepIssues: data.sleep_issues || [],
    appetiteChanges: data.appetite_changes,
    weightChanges: data.weight_changes,
    weightChangeAmount: data.weight_change_amount,
    energyLevel: data.energy_level,
    activityLevel: data.activity_level,
    concentration: data.concentration,
    memory: data.memory,
    decisionMaking: data.decision_making,
    suicidalThoughts: data.suicidal_thoughts,
    suicidalPlan: data.suicidal_plan,
    suicidalIntent: data.suicidal_intent,
    selfHarmThoughts: data.self_harm_thoughts,
    selfHarmBehavior: data.self_harm_behavior,
    homicidalThoughts: data.homicidal_thoughts,
    homicidalPlan: data.homicidal_plan,
    homicidalIntent: data.homicidal_intent,
    currentSubstanceUse: data.current_substance_use || {},
    substanceAbuseHistory: data.substance_abuse_history,
    treatmentHistory: data.treatment_history,
    socialFunctioning: data.social_functioning,
    occupationalFunctioning: data.occupational_functioning,
    relationshipStatus: data.relationship_status,
    traumaHistory: data.trauma_history,
    traumaDetails: data.trauma_details,
    ptsdSymptoms: data.ptsd_symptoms || [],
    culturalBackground: data.cultural_background,
    religiousBeliefs: data.religious_beliefs,
    culturalFactors: data.cultural_factors,
    treatmentGoals: data.treatment_goals || [],
    patientExpectations: data.patient_expectations,
    riskFactors: data.risk_factors || [],
    protectiveFactors: data.protective_factors || [],
    clinicalImpression: data.clinical_impression,
    differentialDiagnosis: data.differential_diagnosis || [],
    treatmentRecommendations: data.treatment_recommendations || [],
    medicationRecommendations: data.medication_recommendations || [],
    therapyRecommendations: data.therapy_recommendations || [],
    followUpPlan: data.follow_up_plan,
    urgencyLevel: data.urgency_level,
    isComplete: data.is_complete,
    riskAssessment: data.risk_assessment,
    urgencyColor: data.urgency_color
  };
};
